import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useDialog } from '@/common/context/DialogContext';
import { useAuthStore } from '@/common/auth/store';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import {
  getGetPeopleProfileQueryKey,
  getGetPeopleProfilePhotoUrlQueryKey,
  useGetPeopleProfile,
  useGetPeopleProfilePhotoUrl,
  usePatchPeopleProfile,
  useUploadPeopleProfilePhoto,
} from '@/common/api/endpoints/users/users';
import type { PeopleProfile } from '@/common/api/models/peopleProfile';
import type { PeopleProfilePatchRequest } from '@/common/api/models/peopleProfilePatchRequest';

import { invalidateAllGssOccasionPeopleQueries } from '../peopleContact';

export type ProfileFormState = {
  shareEmail: string;
  phone: string;
  whatsapp: string;
  sharePhone: boolean;
  shareWhatsapp: boolean;
  showPhotoToConnections: boolean;
  openToConnectGss: boolean;
};

export function hasReachContact(
  form: Pick<ProfileFormState, 'shareEmail' | 'phone' | 'sharePhone'>,
): boolean {
  return Boolean(form.shareEmail.trim() || (form.phone.trim() && form.sharePhone));
}

const emptyForm = (): ProfileFormState => ({
  shareEmail: '',
  phone: '',
  whatsapp: '',
  sharePhone: false,
  shareWhatsapp: false,
  showPhotoToConnections: true,
  openToConnectGss: true,
});

function profileToForm(profile: PeopleProfile): ProfileFormState {
  return {
    shareEmail: profile.shareEmail ?? '',
    phone: profile.phone ?? '',
    whatsapp: profile.whatsapp ?? '',
    sharePhone: profile.sharePhone ?? false,
    shareWhatsapp: profile.shareWhatsapp ?? false,
    showPhotoToConnections: profile.showPhotoToConnections ?? true,
    openToConnectGss: profile.openToConnectGss ?? true,
  };
}

function formToPatch(form: ProfileFormState): PeopleProfilePatchRequest {
  return {
    shareEmail: form.shareEmail.trim() || null,
    phone: form.phone.trim() || null,
    whatsapp: form.whatsapp.trim() || null,
    sharePhone: form.sharePhone,
    shareWhatsapp: form.shareWhatsapp,
    showPhotoToConnections: form.showPhotoToConnections,
    openToConnectGss: hasReachContact(form) && form.openToConnectGss,
  };
}

const EMAIL_RE = /^[^@]+@[^@]+\.[^@]+$/;

function syncAuthProfilePhoto(blobPath: string | null | undefined) {
  useAuthStore.setState((state) => {
    if (!state.user) return {};
    return { user: { ...state.user, profilePhotoBlobPath: blobPath ?? null } };
  });
}

/**
 * Profile form state, save/upload mutations, and preview derivation.
 */
export function useProfileForm() {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id ?? '';
  const queryClient = useQueryClient();
  const { isFeatureActive } = useUserFeatures();
  const connectionsEnabled = isFeatureActive('student-connections') && !!user?.id;

  const profileQuery = useGetPeopleProfile(userId, {
    query: { enabled: connectionsEnabled },
  });
  const profile = profileQuery.data?.data?.data ?? undefined;

  const photoUrlQuery = useGetPeopleProfilePhotoUrl(userId, {
    query: { enabled: connectionsEnabled && !!profile?.profilePhotoBlobPath },
  });

  const [form, setForm] = useState<ProfileFormState>(emptyForm());
  const [savedSnapshot, setSavedSnapshot] = useState<ProfileFormState>(emptyForm());
  const [localPhotoUrl, setLocalPhotoUrl] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    const next = profileToForm(profile);
    setForm(next);
    setSavedSnapshot(next);
  }, [profile]);

  const photoUrl = localPhotoUrl ?? photoUrlQuery.data?.data?.data?.url ?? null;
  const hasStoredPhoto = !!profile?.profilePhotoBlobPath || !!localPhotoUrl;

  const initials = useMemo(() => {
    const first = (user?.firstname ?? profile?.firstName ?? '').trim();
    const last = (user?.lastname ?? '').trim();
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '?';
  }, [user?.firstname, user?.lastname, profile?.firstName]);

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(savedSnapshot),
    [form, savedSnapshot],
  );

  const previewReach = useMemo(() => ({
    email: form.shareEmail.trim() || null,
    phone: form.sharePhone && form.phone.trim() ? form.phone.trim() : null,
    whatsapp: form.shareWhatsapp && form.whatsapp.trim() ? form.whatsapp.trim() : null,
  }), [form]);

  const hasShareableContact = !!(previewReach.email || previewReach.phone || previewReach.whatsapp);
  const canOpenToConnect = hasReachContact(form);

  const saveMutation = usePatchPeopleProfile({
    mutation: {
      onSuccess: (response) => {
        const { data } = response.data;
        if (!data) return;
        const next = profileToForm(data);
        setForm(next);
        setSavedSnapshot(next);
        queryClient.setQueryData(getGetPeopleProfileQueryKey(userId), response);
        invalidateAllGssOccasionPeopleQueries(queryClient);
      },
    },
  });

  const uploadMutation = useUploadPeopleProfilePhoto({
    mutation: {
      onSuccess: (response) => {
        const { data } = response.data;
        queryClient.setQueryData(getGetPeopleProfileQueryKey(userId), response);
        queryClient.invalidateQueries({ queryKey: getGetPeopleProfilePhotoUrlQueryKey(userId) });
        syncAuthProfilePhoto(data?.profilePhotoBlobPath);
      },
      onError: () => {
        showToastMessage('error', t('people.photo.capture.uploadError'));
      },
    },
  });

  const handleSave = useCallback(() => {
    if (profileQuery.isError || !profile) return;
    const trimmedEmail = form.shareEmail.trim();
    if (trimmedEmail && !EMAIL_RE.test(trimmedEmail)) {
      setEmailError(t('people.profile.contacts.email.invalid'));
      return;
    }
    setEmailError(null);
    saveMutation.mutate(
      { userId, data: formToPatch(form) },
      {
        onSuccess: () => showToastMessage('success', t('people.profile.save.success')),
        onError: () => showToastMessage('error', t('people.profile.save.error')),
      },
    );
  }, [form, profile, profileQuery.isError, saveMutation, showToastMessage, t, userId]);

  const handleRemovePhoto = useCallback(async () => {
    setLocalPhotoUrl(null);
    try {
      await saveMutation.mutateAsync({
        userId,
        data: { clearProfilePhoto: true },
      });
      queryClient.removeQueries({ queryKey: getGetPeopleProfilePhotoUrlQueryKey(userId) });
      syncAuthProfilePhoto(null);
    } catch {
      showToastMessage('error', t('people.photo.capture.uploadError'));
    }
  }, [queryClient, saveMutation, showToastMessage, t, userId]);

  const handlePhotoCaptured = useCallback(async (imageBase64: string) => {
    try {
      await uploadMutation.mutateAsync({ userId, data: { imageBase64 } });
      setLocalPhotoUrl(`data:image/jpeg;base64,${imageBase64}`);
    } catch {
      // toast handled in mutation
    }
  }, [uploadMutation, userId]);

  const updateField = useCallback(<K extends keyof ProfileFormState>(key: K, value: ProfileFormState[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'shareEmail' || key === 'phone' || key === 'sharePhone') {
        const hadContact = hasReachContact(prev);
        const hasContact = hasReachContact(next);
        if (!hasContact) {
          next.openToConnectGss = false;
        } else if (!hadContact) {
          next.openToConnectGss = true;
        }
      }
      return next;
    });
    if (key === 'shareEmail') setEmailError(null);
  }, []);

  return {
    form,
    updateField,
    isDirty,
    isLoading: profileQuery.isLoading,
    isError: profileQuery.isError,
    isFetching: profileQuery.isFetching,
    refetch: profileQuery.refetch,
    isSaving: saveMutation.isPending,
    emailError,
    photoUrl,
    hasStoredPhoto,
    initials,
    firstName: user?.firstname ?? profile?.firstName ?? '',
    previewReach,
    hasShareableContact,
    canOpenToConnect,
    handleSave,
    handleRemovePhoto,
    handlePhotoCaptured,
    isUploading: uploadMutation.isPending,
  };
}
